import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';
import { useAuth } from './useAuth';

export interface UserProfile {
  subscriptionTier: string | null;
  subscriptionStatus: string | null;
}

export type SubscriptionTierName = 'free' | 'starter' | 'creator' | 'publisher' | null;

export const TIER_ORDER: Record<string, number> = {
  free: 0,
  starter: 1,
  creator: 2,
  publisher: 3,
};

export function useUserProfile() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<UserProfile>>('/users/me');
      // Return null (not undefined) when no user doc exists.
      // RQ v5 throws if queryFn returns undefined; callers already handle null.
      return res.data.data ?? null;
    },
    enabled: isAuthenticated,
    staleTime: 60_000, // 1 min — subscription tier rarely changes mid-session
  });
}

export function isActiveSubscriber(profile: UserProfile | undefined | null): boolean {
  return (
    !!profile &&
    profile.subscriptionTier !== null &&
    profile.subscriptionTier !== 'free' &&
    profile.subscriptionStatus === 'active'
  );
}

export function isCreatorPlus(profile: UserProfile | undefined | null): boolean {
  return (
    !!profile &&
    (profile.subscriptionTier === 'creator' || profile.subscriptionTier === 'publisher') &&
    profile.subscriptionStatus === 'active'
  );
}
